import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Image, Flex, Text } from "theme-ui";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import "../../../src/utils/glassToken.css";
import { colors, typography } from "../../styles/theme";
import { Icon } from "../atoms";

const ProfileCard = ({
  image,
  imageAltText,
  imageUrl,
  name,
  designation,
  bio,
  twitterUrl,
  linkedinUrl,
  customStyles,
  variant = "default",
  ctaTitle,
  onCTAClick,
  customCTAStyles
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const openSocialLink = (url) => {
    if (typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const renderSocialIcon = (url, iconName) => (
    <Box onClick={() => openSocialLink(url)} sx={iconContainerStyles}>
      <Icon
        name={iconName}
        size={12}
        color={colors.white}
        pointerCursor
        customStyles={socialMediaIconStyles}
      />
    </Box>
  );

  return (
		<Box
			sx={{
				...containerStyles,
				...customStyles,
			}}
		>
			{!expanded && (
				<Box sx={collapsedBoxStyles}>
					{imageUrl && (
						<Image
							src={imageUrl}
							alt="Profile Photo"
							sx={placeholderImageStyles}
              className="card-image"
						/>
					)}
					{image && (
						<GatsbyImage
							image={getImage(image)}
							style={imageContainer}
							imgStyle={imageStyle}
							alt={imageAltText}
              className="card-image"
						/>
					)}

					<Box
						className="glass300"
						sx={{
							...infoBoxStyles,
							minHeight: variant === "default" ? "14.469rem" : "max-content",
						}}
					>
						<Icon
							name="Maximize2"
							pointerCursor
							onClick={handleExpand}
							customStyles={maximizeIconStyles}
						/>
						{name && <Text sx={profileNameStyles} className="profile-card-title">{name}</Text>}
						{designation && (
							<Text sx={profileDesignationStyles}>{designation}</Text>
						)}

						{(linkedinUrl || twitterUrl) && (
							<Flex sx={socialMediaIconContainer}>
								{linkedinUrl && renderSocialIcon(linkedinUrl, "Linkedin")}
								{twitterUrl && renderSocialIcon(twitterUrl, "Twitter")}
							</Flex>
						)}

						{bio && variant === "default" && (
							<Text
								as="p"
								sx={profileBioCollapsedStyles}
								dangerouslySetInnerHTML={{ __html: bio }}
							/>
						)}
					</Box>
				</Box>
			)}
			{expanded && (
				<Box
					className="glass300"
					sx={expandedBoxStyles}
				>
					<Icon
						name="Minimize2"
						pointerCursor
						onClick={handleExpand}
						customStyles={minimizeIconStyles}
					/>
					{name && variant === "default" && (
						<Text sx={profileNameStyles}>{name}</Text>
					)}
					{bio && (
            <Flex sx={{
              height: "100%",
              alignItems: variant === "default" ? "flex-start" : "center",
              flexDirection:"column"
            }}>
              <Text
                as="p"
                sx={
                  image
                    ? profileBioExpandedStyles
                    : profileBioExpandedStorybookStyles
                }
                dangerouslySetInnerHTML={{ __html: bio }}
              />
               {ctaTitle && (
                <Text sx={{...ctaStyles,...customCTAStyles}} onClick={onCTAClick}>
                  {ctaTitle}
                </Text>
              )}
            </Flex>
					)}
				</Box>
			)}
		</Box>
	);
};

const {
  containerStyles,
  collapsedBoxStyles,
  placeholderImageStyles,
  imageContainer,
  imageStyle,
  infoBoxStyles,
  maximizeIconStyles,
  profileNameStyles,
  profileDesignationStyles,
  socialMediaIconContainer,
  iconContainerStyles,
  socialMediaIconStyles,
  profileBioCollapsedStyles,
  expandedBoxStyles,
  minimizeIconStyles,
  profileBioExpandedStyles,
  profileBioExpandedStorybookStyles,
  ctaStyles,
} = {
  containerStyles: {
    width: "23.125rem",
    height: "32.5rem",
    borderRadius: "16px",
    padding: "1rem",
    backgroundColor: colors.white,
    "@media screen and (max-width: 371px)": {
      width: "100%",
    },
  },
  collapsedBoxStyles: {
    position: "relative",
    height: "100%",
  },
  placeholderImageStyles: {
    width: "100%",
    height: "70%",
    objectFit: "cover",
    borderRadius: "16px",
  },
  imageContainer: {
    width: "100%",
  },
  imageStyle: {
    borderRadius: "16px",
  },
  infoBoxStyles: {
    position: "absolute",
    bottom: 0,
    padding: "1.625rem 1.25rem",
    width: "100%",
  },
  maximizeIconStyles: {
    position: "absolute",
    right: "1rem",
    top: "1rem",
  },
  profileNameStyles: {
    display: "inline-block",
    width: "94%",
    ...typography.textLg500,
  },
  profileDesignationStyles: {
    display: "inline-block",
    width: "100%",
    ...typography.textSm,
    mt: "0.25rem",
  },
  socialMediaIconContainer: {
    gap: "0.25rem",
    mt: "0.5rem"
  },
  iconContainerStyles: {
    position: "relative",
    width: "1.25rem",
    height: "1.25rem",
    backgroundColor: colors.black,
    borderRadius: "50%",
  },
  socialMediaIconStyles: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fill: colors.white,
  },
  profileBioCollapsedStyles: {
    ...typography.textMd400,
    marginTop: "1.5rem",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 4,
    WebkitBoxOrient: "vertical",
    "& p": {
      margin: "0",
    },
  },
  expandedBoxStyles: {
    width: "100%",
    height: "100%",
    padding: "1.625rem 1.25rem",
    position: "relative",
  },
  minimizeIconStyles: {
    position: "absolute",
    right: "1rem",
    top: "1rem"
  },
  profileBioExpandedStyles: {
    ...typography.textMd400,
    marginTop: "1.5rem",
    "& p": {
      margin: "0",
      overflowY: "auto",
      maxHeight: "23.7rem",
      paddingRight: "0.313rem"
    },
  },
  profileBioExpandedStorybookStyles: {
    ...typography.textMd400,
    marginTop: "1.5rem",
    overflowY: "auto",
    maxHeight: "23.7rem",
    paddingRight: "0.313rem"
  },
  ctaStyles: {
    ...typography.h6,
    color: colors.primaryMain,
    alignSelf: "flex-start",
    mt: "1rem",
    cursor: "pointer",
  },
};

ProfileCard.propTypes = {
  image: PropTypes.object,
  imageAltText: PropTypes.string,
  imageUrl: PropTypes.string,
  name: PropTypes.string,
  designation: PropTypes.string,
  bio: PropTypes.string,
  twitterUrl: PropTypes.string,
  linkedinUrl: PropTypes.string,
  customStyles: PropTypes.object,
  variant: PropTypes.oneOf(["default", "fullImage"]),
};

export default ProfileCard;
